// Purpose: Build a copy-paste GTIN list from selected catalog items for Walmart's Convert to WSF modal.
// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from 'react'
import Loading from './Loading';
import { capitalizeFLetter, clipboard } from '../../fnc'

const initialState = {
	items: [],
	reqDone: null,
}

const specialVar = {
	_isMounted: false
}

// Walmart expects 14-digit GTIN values (same padding used in api tmpwork scripts).
const toGtin14 = (barcode) => {
	if(barcode === null || barcode === undefined || barcode === '')
		return null

	const digits = String(barcode).replace(/\D/g, '')
	if(!digits)
		return null

	if(digits.length > 14)
		return digits.slice(-14)

	return digits.padStart(14, '0')
}

const chunkList = (list, size) => {
	const chunks = []
	for(let i = 0; i < list.length; i += size)
		chunks.push(list.slice(i, i + size))
	return chunks
}

export default function WalmartGtinList(props){

	const [items, setItems] = useState(initialState.items.slice(0))
	const [reqDone, setReqDone] = useState(initialState.reqDone)
	const [version, setVersion] = useState("1.0.0")
	const [refreshView, setRefreshView] = useState(0)

	useEffect(() => {
		specialVar._isMounted = true
		return () => specialVar._isMounted = false
	}, [])

	useEffect(() => {
		modal(true)
	}, [items, reqDone, refreshView])

	const resetPage = () => {
		for (const key in initialState) {
			if (initialState.hasOwnProperty(key)) {
				let val = JSON.parse(JSON.stringify(initialState[key]))
				eval("set" + capitalizeFLetter(key))(val)
			}
		}
	}

	const buildRows = () => {
		return items.map((item) => {
			const barcode = item.upc || item.ean || null
			return {
				id: item.id,
				sku: item.sku,
				title: item.title,
				barcode,
				gtin: toGtin14(barcode),
			}
		})
	}

	const init = () => {
		if(props.itemsSelected.length === 0){
			props.info({error: "Please select some items first."})
			return
		}

		setReqDone(false)
		resetPage()
		props.loading(true)

		props.fetchItems({
			trigger: "getItemsCatalog_v2",
			start: 0,
			limit: props.total_items,
			idList: props.itemsSelected.join(',')
		}, (r) => {
			if(!specialVar._isMounted)
				return

			props.loading(false)
			setReqDone(true)

			if(!r.success){
				props.info({error: r.error || "Failed to fetch selected items."})
				setItems(initialState.items.slice(0))
				return
			}

			setItems(r.data || initialState.items.slice(0))
		})

		modal(false)
	}

	const renderContent = () => {
		const rows = buildRows()
		const gtinList = rows.map((row) => row.gtin).filter(Boolean)
		const missingRows = rows.filter((row) => !row.gtin)
		const gtinChunks = chunkList(gtinList, 10)

		return <div className="container-fluid">
			<div className="alert alert-info" role="alert">
				Copy the GTIN list below and paste it into Walmart&apos;s <strong>Convert to WSF</strong> modal.
				GTINs are formatted as 14-digit values from each item&apos;s UPC/EAN.
				{gtinChunks.length > 1? <span> Lists are split into groups of 10 when Walmart limits batch size.</span> : ''}
			</div>

			<div className="d-flex flex-wrap mb-3">
				<button
					type="button"
					className="btn btn-primary mr-2 mb-2"
					disabled={gtinList.length === 0}
					onClick={() => clipboard(gtinList.join(','), props)}
				>
					Copy all GTINs ({gtinList.length})
				</button>
				{gtinChunks.map((chunk, index) => (
					<button
						key={`gtin-chunk-${index}`}
						type="button"
						className="btn btn-outline-primary mr-2 mb-2"
						onClick={() => clipboard(chunk.join(','), props)}
					>
						Copy group {index + 1} ({chunk.length})
					</button>
				))}
			</div>

			{gtinChunks.map((chunk, index) => (
				<div key={`gtin-textarea-${index}`} className="mb-3">
					<label className="font-weight-bold">Group {index + 1}</label>
					<textarea
						className="form-control"
						readOnly
						rows={Math.min(4, Math.max(2, Math.ceil(chunk.length / 5)))}
						value={chunk.join(',')}
					/>
				</div>
			))}

			{missingRows.length > 0? <div className="alert alert-warning" role="alert">
				{missingRows.length} selected item(s) have no UPC/EAN and were skipped.
			</div> : ''}

			<div className="table-responsive">
				<table className="table table-sm table-bordered">
					<thead className="bg-light">
						<tr>
							<th>SKU</th>
							<th>Title</th>
							<th>UPC/EAN</th>
							<th>GTIN (14-digit)</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.id} className={row.gtin? '' : 'table-warning'}>
								<td>{row.sku}</td>
								<td>{row.title}</td>
								<td>{row.barcode || '-'}</td>
								<td>
									{row.gtin? <>
										<span>{row.gtin}</span>
										<button type="button" className="btn btn-link btn-sm p-0 ml-2" onClick={() => clipboard(row.gtin, props)}>Copy</button>
									</> : 'Missing'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	}

	const modal = (refresh) => {
		props.modal({
			show: refresh? false : true,
			title: <span>Walmart GTIN list <h6 className="text-secondary d-inline ml-3" style={{fontSize: "12px", textShadow: "0px 0px 4px #FFF"}}>v{version}</h6></span>,
			options: {
				width: "modal-xl"
			},
			html: () => {
				return reqDone !== true? <Loading loading={true} centered={true}/> : renderContent()
			},
			exit: (popup, close) => {
				close()
			},
			return: {
				title: "Close",
				fnc: (popup, close) => {
					resetPage()
					close()
				}
			}
		})
	}

	return (<div className={"className" in props? props.className : 'list-group-item list-group-item-action p-2 text-center'} onClick={() => init()}>
		{props.title || "GTIN list for WSF"}
	</div>)
}
